import React from 'react';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * Liste ordonnée des questions d'un formulaire, réordonnable au glisser-déposer.
 * Même patron que WorkflowStepList : la poignée ⠿ porte seule les listeners,
 * sinon le moindre clic dans un champ déclencherait un tri.
 */

// Clé stable pour @dnd-kit : une question pas encore enregistrée n'a pas d'id
// en base, on lui donne une clé locale à la création.
const cle = (q) => (q.id ? `q${q.id}` : q._cle);

function SortableQuestion({ question, types, onChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cle(question) });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const verrouillee = (question.nb_reponses || 0) > 0;

  return (
    <div ref={setNodeRef} style={style} className="sat-question-card">
      <button type="button" className="sat-drag" title="Glisser pour réordonner" {...attributes} {...listeners}>⠿</button>
      <div className="sat-question-body">
        <div className="sat-question-ligne">
          <input
            className="sat-input sat-input-libelle"
            value={question.libelle}
            placeholder="Intitulé de la question"
            onChange={(e) => onChange({ libelle: e.target.value })}
          />
          <select
            className="sat-input sat-input-type"
            value={question.type}
            disabled={verrouillee}
            title={verrouillee ? 'Type figé : cette question porte déjà des réponses' : undefined}
            onChange={(e) => onChange({ type: e.target.value })}
          >
            {types.map((t) => <option key={t.cle} value={t.cle}>{t.label}</option>)}
          </select>
        </div>
        <div className="sat-question-ligne">
          <input
            className="sat-input"
            value={question.aide || ''}
            placeholder="Consigne affichée sous la question (facultatif)"
            onChange={(e) => onChange({ aide: e.target.value })}
          />
          {question.type !== 'recontact' && (
            <label className="sat-check">
              <input
                type="checkbox"
                checked={question.obligatoire !== false}
                onChange={(e) => onChange({ obligatoire: e.target.checked })}
              />
              Obligatoire
            </label>
          )}
          <button
            type="button"
            className="btn-icon delete"
            title={verrouillee
              ? 'Cette question porte des réponses : elle sera retirée du questionnaire mais ses réponses resteront lisibles'
              : 'Retirer la question'}
            onClick={onRemove}
          >🗑️</button>
        </div>
        {question.type === 'recontact' && (
          <div className="sat-question-note hint">
            Les cases (téléphone, SMS, e-mail) et leurs textes viennent d'A97 RGPD, au nom de la société
            qui a vendu ou réparé : c'est elle qui obtient le consentement. Seuls les canaux dont on a la
            coordonnée sont proposés. Chaque case cochée est enregistrée dans A97 RGPD avec sa preuve
            (le texte exact lu par le client) ; une case vide n'enregistre rien. Jamais obligatoire : un
            consentement doit rester libre. Si A97 RGPD ne répond pas, le bloc n'est pas affiché.
          </div>
        )}
        {verrouillee && (
          <div className="sat-question-note">
            {question.nb_reponses} réponse(s) déjà collectée(s) — le type ne peut plus changer.
          </div>
        )}
      </div>
    </div>
  );
}

export default function SatisfactionQuestionList({ questions, types, onChange }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const majAt = (i, patch) => onChange(questions.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const retirerAt = (i) => onChange(questions.filter((_, j) => j !== i));

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = questions.findIndex((q) => cle(q) === active.id);
    const to = questions.findIndex((q) => cle(q) === over.id);
    if (from < 0 || to < 0) return;
    onChange(arrayMove(questions, from, to));
  };

  return (
    <div className="sat-questions">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={questions.map(cle)} strategy={verticalListSortingStrategy}>
          {questions.map((q, i) => (
            <SortableQuestion
              key={cle(q)}
              question={q}
              types={types}
              onChange={(patch) => majAt(i, patch)}
              onRemove={() => retirerAt(i)}
            />
          ))}
        </SortableContext>
      </DndContext>
      {questions.length === 0 && (
        <div className="sat-vide">Aucune question. Ajoutez-en une pour que le formulaire puisse partir.</div>
      )}
    </div>
  );
}
